import React, { useContext, useState, useEffect } from 'react';  
import '../styles/createquiz.css';  
import Alert from '../components/Alert';  
import Spinner from '../components/Spinner';  
import { useNavigate } from "react-router-dom";  
import style from '../styles/quizesPage.css';  
import ResultContext from '../context/ResultContext';  

export default function CreateQuiz() {  
  const HOST = process.env.REACT_APP_HOST_NAME;  
  const navigate = useNavigate();  
  const [quizText, setQuizText] = useState("");  
  const [quizJson, setQuizJson] = useState({ caption: "", subject: "", questions: [] });  
  const [batches, setBatches] = useState([]);  
  const [isPremium, setIsPremium] = useState(false);  
  const [selectedBatchId, setSelectedBatchId] = useState("");  
  const [loadingBatches, setLoadingBatches] = useState(false);  
  const [savingQuiz, setSavingQuiz] = useState(false);  
  const { setAlertContext } = useContext(ResultContext);  

  // ✅ Subject options from your model
  const subjectOptions = [
    'Geography',
    'CurrentAffairs',
    'History',
    'MathReasoning',
    'MarathiGrammar',
    'Science',
    'Polity',
    'PoliceBharti',
    'Economics',
    'English',
    'ComputerIT',
    'BankingExams',
    'GovernmentJobs',
    'GeneralKnowledge',
    'Other'
  ];

  // ✅ Fetch premium batches when premium is selected
  useEffect(() => {  
    if (isPremium) {  
      fetchPremiumBatches();
    } else {
      setBatches([]);
      setSelectedBatchId("");
    }
  }, [isPremium]);  

  const fetchPremiumBatches = async () => {
    setLoadingBatches(true);  
    try {
      const response = await fetch(`${HOST}api/fetch-premium-batches`, {  
        method: 'GET',  
        headers: {  
          'Content-Type': 'application/json',  
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }  
      });
      const data = await response.json();
      if (!response.ok) {  
        throw new Error(data.message || 'Failed to fetch batches');  
      }  
      setBatches(data.batches || data || []);  
    } catch (error) {
      showAlert(error.message || 'There was a problem fetching premium batches', 'danger');  
      console.error('Error fetching premium batches:', error);  
    } finally {
      setLoadingBatches(false);  
    }
  };

  const showAlert = (message, status) => {  
    setAlertContext({  
      isActive: true,  
      message: message,  
      status: status  
    });  
    setTimeout(() => {  
      setAlertContext(prevState => ({ ...prevState, isActive: false }));  
    }, 2000);  
  };  

  // ✅ UPDATED: Parse questions - only caption from first line
  const parseQuestions = (input) => {  
    const lines = input.split('\n').filter(line => line.trim() !== '');  
    
    // ✅ First line is caption
    const caption = lines.shift()?.trim();
    
    if (!caption) {
      throw new Error('Caption is required on the first line');
    }
    
    const questions = [];  
    let currentQuestion = null;  
    let questionText = '';  

    lines.forEach(line => {  
      const questionMatch = line.match(/^\(\d+\) (.*)/);  
      if (questionMatch) {  
        if (currentQuestion) {  
          currentQuestion.question = questionText.trim();  
          questions.push(currentQuestion);  
        }  
        currentQuestion = { question: "", options: [], answer: "", description: "" };  
        questionText = questionMatch[1];  
      } else if (currentQuestion) {  
        const optionMatch = line.match(/^([A-D])\. (.*)/);  
        if (optionMatch) {  
          let optionText = optionMatch[2].replace(' √', '').replace('√', '').replace('√√', '').trim();  
          currentQuestion.options.push(optionText);  
          if (line.includes('√')) {  
            currentQuestion.answer = (optionMatch[1].charCodeAt(0) - 'A'.charCodeAt(0) + 1).toString();  
          }  
        } else {  
          questionText += ' ' + line.trim();  
        }  
      }  
    });  

    if (currentQuestion) {  
      currentQuestion.question = questionText.trim();  
      questions.push(currentQuestion);  
    }  

    return {  
      caption: caption,  
      questions: questions  
    };  
  };  

  const handleTextChange = (event) => {  
    setQuizText(event.target.value);  
  };  

  const createQuiz = () => {  
    try {  
      const parsedQuiz = parseQuestions(quizText);  
      if (!parsedQuiz.caption || parsedQuiz.questions.length === 0) {
        showAlert('Please enter valid quiz text with caption and questions', 'danger');
        return;
      }
      // ✅ Keep existing subject if already selected
      setQuizJson({
        ...parsedQuiz,
        subject: quizJson.subject || '' // Preserve selected subject
      });  
      showAlert('Quiz parsed successfully. Please select a subject and review the questions.', 'success');  
    } catch (error) {  
      showAlert(error.message || 'There was a problem parsing the quiz text. Please check the format and try again.', 'danger');  
      console.error('Parse error:', error);
    }  
  };  

  const handleQuestionChange = (index, key, value) => {  
    const updatedQuiz = { ...quizJson };  
    if (index === -1) {  
      updatedQuiz[key] = value;  
    } else {  
      updatedQuiz.questions[index][key] = value;  
    }  
    setQuizJson(updatedQuiz);  
  };  

  const handleOptionChange = (questionIndex, optionIndex, value) => {  
    const updatedQuiz = { ...quizJson };  
    updatedQuiz.questions[questionIndex].options[optionIndex] = value;  
    setQuizJson(updatedQuiz);  
  };  

  const handleCorrectOptionChange = (questionIndex, value) => {  
    const updatedQuiz = { ...quizJson };  
    updatedQuiz.questions[questionIndex].answer = value;  
    setQuizJson(updatedQuiz);  
  };  

  const handleCheckboxChange = (event) => {  
    setIsPremium(event.target.checked);  
  };  

  const handleBatchSelectChange = (event) => {  
    setSelectedBatchId(event.target.value);  
  };  

  // ✅ Handle subject dropdown change
  const handleSubjectChange = (event) => {
    handleQuestionChange(-1, 'subject', event.target.value);
  };

  const saveQuiz = async () => {  
    // ✅ Validation
    if (!quizJson.caption || !quizJson.caption.trim()) {
      showAlert('Caption is required', 'danger');
      return;
    }

    // ✅ Subject validation - MANDATORY
    if (!quizJson.subject) {
      showAlert('Please select a subject before submitting', 'danger');
      return;
    }

    if (quizJson.questions.length === 0) {
      showAlert('Please create a quiz with questions first', 'danger');
      return;
    }

    if (isPremium && !selectedBatchId) {
      showAlert('Please select a premium batch', 'danger');
      return;
    }

    // Check if all questions have answers
    const hasInvalidQuestions = quizJson.questions.some(q => !q.answer || q.options.length < 2);
    if (hasInvalidQuestions) {
      showAlert('Please ensure all questions have options and correct answers', 'danger');
      return;
    }

    setSavingQuiz(true);
    try {
      const url = isPremium 
        ? `${HOST}api/create-premium-quiz`
        : `${HOST}api/createquiz`;

      const quizToSave = isPremium 
        ? { ...quizJson, batchId: selectedBatchId }
        : quizJson;

      console.log("Saving quiz:", quizToSave);

      const response = await fetch(url, {  
        method: 'POST',  
        headers: {  
          'Content-Type': 'application/json',  
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },  
        body: JSON.stringify(quizToSave),  
      });

      const data = await response.json();

      if (!response.ok) {  
        throw new Error(data.message || 'Failed to create quiz');  
      }  

      showAlert('Quiz Created Successfully!', 'success');  

      // ✅ Navigate after short delay
      setTimeout(() => {
        if (isPremium && selectedBatchId) {
          navigate(`/premium-tests/${selectedBatchId}`);  
        } else {
          navigate('/adminpage');
        }
      }, 1000);

    } catch (error) {  
      showAlert(error.message || 'There was a problem creating the quiz', 'danger');  
      console.error('Error creating quiz:', error);  
    } finally {
      setSavingQuiz(false);
    }
  };  

  return (  
    <div className="container">  
      <div className="header">  
        <h1>Welcome Admin! Create Today's Quiz.</h1>  
      </div>  
      
      <h1>Enter Test Text</h1>  
      <p className="text-muted">
        <strong>Format:</strong><br/>
        Line 1: Caption (e.g., Indian Rivers and Mountains Quiz)<br/>
        Then questions in format: (1) Question text...<br/>
        <em>Note: You will select the subject from dropdown after parsing.</em>
      </p>
      
      <textarea  
        className='container'  
        rows="20"  
        cols="100"  
        value={quizText}  
        onChange={handleTextChange}  
        placeholder="Indian Rivers and Mountains - Practice Quiz
(1) Which is the longest river in India?
A. Ganga √
B. Yamuna
C. Godavari
D. Brahmaputra
(2) Mount Everest is located in which mountain range?
A. Western Ghats
B. Eastern Ghats
C. Himalayas √
D. Aravalli"
      />  
      <br />  
      
      <button onClick={createQuiz} disabled={!quizText.trim()}>
        Parse Questions
      </button>  

      {quizJson.questions.length > 0 && (  
        <div>  
          <h2>Review and Edit Quiz</h2>  
          
          {/* ✅ Subject Dropdown - MANDATORY */}
          <div className="mb-3">  
            <label><strong>Subject: * (Required)</strong></label>  
            <select  
              className="form-control"
              value={quizJson.subject || ''}  
              onChange={handleSubjectChange}
              required
              style={{ 
                borderColor: !quizJson.subject ? '#dc3545' : '#ced4da',
                borderWidth: !quizJson.subject ? '2px' : '1px'
              }}
            >  
              <option value="" disabled>-- Select Subject --</option>
              {subjectOptions.map((subject, index) => (
                <option key={index} value={subject}>
                  {subject}
                </option>
              ))}
            </select>
            {!quizJson.subject && (
              <small className="text-danger">⚠️ Please select a subject to continue</small>
            )}
          </div>

          {/* ✅ Caption Input */}
          <div className="mb-3">  
            <label><strong>Caption: *</strong></label>  
            <input  
              type="text"  
              className="form-control"
              value={quizJson.caption || ''}  
              onChange={(e) => handleQuestionChange(-1, 'caption', e.target.value)}  
              placeholder="Enter quiz caption (e.g., Indian Rivers and Mountains Quiz)"
            />  
          </div>

          {/* Premium Checkbox */}
          <div className="mt-3 mb-3">
            <label>  
              <input  
                type="checkbox"  
                checked={isPremium}  
                onChange={handleCheckboxChange}  
              />  
              {' '}Premium Quiz
            </label>  
          </div>

          {/* Premium Batch Selection */}
          {isPremium && (  
            <div className="mb-3">  
              <label><strong>Select Premium Batch: *</strong></label>  
              {loadingBatches ? (  
                <Spinner />  
              ) : (  
                <select  
                  className="form-control"
                  value={selectedBatchId}  
                  onChange={handleBatchSelectChange}  
                  required
                >  
                  <option value="" disabled>-- Select Batch --</option>  
                  {batches.map(batch => (  
                    <option key={batch._id} value={batch._id}>   
                      {batch.name} - {batch.subject}
                    </option>  
                  ))}  
                </select>  
              )}  
            </div>  
          )}  

          <hr />

          {/* Questions List */}
          <h3>Questions ({quizJson.questions.length})</h3>
          {quizJson.questions?.map((question, qIndex) => (  
            <div key={qIndex} className="question-container">  
              <h4>Question {qIndex + 1}</h4>
              <textarea  
                rows="3"  
                cols="100"  
                className="form-control mb-2"
                value={question.question || ''}  
                onChange={(e) => handleQuestionChange(qIndex, 'question', e.target.value)}  
                placeholder="Enter question text"
              />  
              
              <div className="options-list">
                {question.options?.map((option, oIndex) => (  
                  <div key={oIndex} className="option-container mb-2">  
                    <label><strong>Option {String.fromCharCode(65 + oIndex)}:</strong></label>
                    <input  
                      type="text"  
                      className="form-control"
                      value={option || ''}  
                      onChange={(e) => handleOptionChange(qIndex, oIndex, e.target.value)}  
                      placeholder={`Option ${String.fromCharCode(65 + oIndex)}`}
                    />  
                  </div>  
                ))}  
              </div>

              <div className="correct-answer mb-3">
                <label><strong>Correct Option:</strong></label>  
                <select  
                  className="form-control"
                  value={question.answer || ''}  
                  onChange={(e) => handleCorrectOptionChange(qIndex, e.target.value)}  
                >  
                  <option value="">Select correct answer</option>
                  {question.options?.map((_, oIndex) => (  
                    <option key={oIndex} value={(oIndex + 1).toString()}>
                      {String.fromCharCode(65 + oIndex)} - Option {oIndex + 1}
                    </option>  
                  ))}  
                </select>  
              </div>
              <hr />
            </div>  
          ))}  

          {/* ✅ Submit Button - Disabled if subject not selected */}
          <button 
            onClick={saveQuiz} 
            disabled={
              savingQuiz || 
              !quizJson.subject || 
              !quizJson.caption || 
              (isPremium && !selectedBatchId)
            }
            className="btn btn-primary mt-3"
            style={{
              opacity: (!quizJson.subject || !quizJson.caption) ? 0.5 : 1,
              cursor: (!quizJson.subject || !quizJson.caption) ? 'not-allowed' : 'pointer'
            }}
          >
            {savingQuiz ? 'Saving...' : 'Save and Submit Quiz'}
          </button>

          {!quizJson.subject && (
            <p className="text-danger mt-2">
              <strong>⚠️ Please select a subject before submitting the quiz</strong>
            </p>
          )}
        </div>  
      )}  
    </div>  
  );  
}